import { useState, useEffect } from 'react';
import { MonthNav } from '../../components/MonthNav/MonthNav';
import { getCurrentMonthKey } from '../../domain/workouts/types';
import { getPreviousMonthKey, getNextMonthKey } from '../../domain/leaderboard/types';
import { useMonthlyHistory } from '../history/useMonthlyHistory';
import { HistoryView } from '../history/HistoryView';
import { TrophyCase } from '../awards/TrophyCase';
import { useAwardsData } from '../awards/useAwardsData';
import { buildTrophyCase } from '../../domain/awards/computeAwards';
import '../history/HistoryPage.css';
import '../awards/Awards.css';
import './UserHistoryModal.css';

export interface UserHistoryModalProps {
  userId: string;
  displayName: string;
  /** Month the leaderboard is currently showing; the modal opens on this month. */
  initialMonthKey: string;
  onClose: () => void;
}

export function UserHistoryModal({
  userId,
  displayName,
  initialMonthKey,
  onClose,
}: UserHistoryModalProps) {
  const currentMonthKey = getCurrentMonthKey();
  const [activeMonthKey, setActiveMonthKey] = useState(initialMonthKey);
  /** The modal shows either their log or their trophy case, never both. */
  const [showTrophies, setShowTrophies] = useState(false);

  const { data: awardsData, isLoading: awardsLoading } = useAwardsData();
  const trophyMonths = awardsData
    ? buildTrophyCase(awardsData.workouts, awardsData.users, userId)
    : [];

  const { workouts, summary, previousSummary, isLoading } = useMonthlyHistory(
    userId,
    activeMonthKey,
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isCurrentMonth = activeMonthKey === currentMonthKey;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="user-history-modal__overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-history-modal-title"
    >
      <div className="user-history-modal__container">
        <div className="user-history-modal__handle" />

        <button
          className="user-history-modal__close"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          &times;
        </button>

        <div className="user-history-modal__heading">
          <h2 id="user-history-modal-title" className="user-history-modal__title">
            {displayName}
          </h2>
          {/* The same control as the leaderboard's, doing the same job one level
              down: swap what is on screen for the trophies behind it. */}
          <button
            type="button"
            className={`trophy-toggle trophy-toggle--sm ${showTrophies ? 'trophy-toggle--on' : ''}`}
            onClick={() => setShowTrophies((on) => !on)}
            aria-pressed={showTrophies}
            aria-label={
              showTrophies ? `Show ${displayName}'s lifts` : `Show ${displayName}'s trophies`
            }
          >
            🏆
          </button>
        </div>

        {!showTrophies && (
          <MonthNav
            monthKey={activeMonthKey}
            onPrevious={() => setActiveMonthKey(getPreviousMonthKey(activeMonthKey))}
            onNext={() => {
              if (!isCurrentMonth) setActiveMonthKey(getNextMonthKey(activeMonthKey));
            }}
            isNextDisabled={isCurrentMonth}
          />
        )}

        <div className="user-history-modal__content">
          {showTrophies ? (
            <TrophyCase months={trophyMonths} isLoading={awardsLoading} earnedOnly />
          ) : (
            <HistoryView
              workouts={workouts}
              summary={summary}
              previousSummary={previousSummary}
              isLoading={isLoading}
              isCurrentMonth={isCurrentMonth}
            />
          )}
        </div>
      </div>
    </div>
  );
}
